/* eslint-disable max-statements */
/* eslint-disable camelcase */
import {cloneDeep} from 'lodash-es';
import eventBus, {FLOW_JS_LOADED} from '@/eventBus';
import type {CommentItem} from '@/interface';
import {flowObj} from './flowObj';
import {normalizeEmbedInfo, normalizeEmbedInlineInfo} from './utils';
import type {IRecognizeLinksOptions, IUrlInfo, IEmbedInlineDataTransfer, IEmbedIframeInfo} from './type.d';

interface IEmbedInlineRecognized {
    'embed-inline'?: {
        title: string;
        dataTransfer: IEmbedInlineDataTransfer;
    };
}

interface IMentionRecognized {
    mention?: {
        name: string;
        docType: string;
        id: string;
        token: string;
        avatar: string;
    };
}
interface IEmbedRecognized {
    embed?: {
        embedType: string;
        iframeInfo: IEmbedIframeInfo;
        title: string;
    };
}

type Recognized = IMentionRecognized & IEmbedInlineRecognized & IEmbedRecognized;

interface IUnifiedLinkRecognitionObject {
    source: string;
    recognized: Recognized;
}

const recognizeType: any = {
    mention: ['TEXT_LINK'],
    'embed-inline': ['BLOCK', 'BLOCK_WORK_CARD'],
    embed: ['IFRAME'],
    default: ['TEXT_LINK', 'BLOCK', 'IFRAME', 'BLOCK_WORK_CARD'],
};

export const recognizeLinks = async ({
    docGuid,
    urls,
    options = {},
}: {
    docGuid: string;
    urls: string[];
    options?: IRecognizeLinksOptions;
}) => {
    const {type, from} = options;
    const anchor = 'sdk$$';

    try {
        // 等待 flow.js 加载完成（如果正在加载中）
        if (!flowObj.getFlowApi()) {
            await new Promise<void>((resolve) => {
                eventBus.once(FLOW_JS_LOADED, () => resolve());
            });
        }

        const flow = flowObj.getFlowApi();

        if (!flow) {
            throw Error('flowJS 资源加载失败');
        }

        if (!flowObj.isRecognizeSupported()) {
            return [];
        }

        const urlInfos: IUrlInfo[] = await flow.linkRecognize.recognize?.({
            renderType: type ? recognizeType[type] : recognizeType.default,
            urls,
            from,
            docId: docGuid,
        });

        if (!Array.isArray(urlInfos)) {
            return [];
        }

        const result: IUnifiedLinkRecognitionObject[] = [];

        urlInfos.forEach((urlInfo) => {
            const {url, TEXT_LINK, BLOCK, IFRAME, BLOCK_WORK_CARD} = urlInfo;

            const recognized: Recognized = {};

            if (TEXT_LINK?.userData) {
                recognized.mention = {
                    name: TEXT_LINK.userData?.name,
                    docType: 'sdk',
                    id: `${Date.now()}`,
                    token: anchor + encodeURIComponent(url),
                    avatar: TEXT_LINK.userData?.icon,
                };
            }

            if (BLOCK?.blockId) {
                recognized['embed-inline'] = {
                    title: BLOCK.title || 'Block',
                    dataTransfer: {
                        recognize_id: BLOCK.blockId,
                        recognize_type: BLOCK?.type,
                        type: 'BLOCK',
                    },
                };
            }

            if (IFRAME?.userData) {
                IFRAME.originUrl = url;
                recognized.embed = {
                    embedType: 'flowIframe',
                    iframeInfo: normalizeEmbedInfo(IFRAME),
                    title: IFRAME.userData?.block_info?.title || '',
                };
            }

            if (BLOCK_WORK_CARD?.userData && BLOCK_WORK_CARD?.urlValid) {
                const data = normalizeEmbedInlineInfo(BLOCK_WORK_CARD);
                recognized['embed-inline'] = {
                    title: data.title || 'BlockWorkCard',
                    dataTransfer: {
                        recognize_id: `blockworkcard-id-${Date.now()}`,
                        recognize_type: data.from,
                        type: 'BLOCK_WORK_CARD',
                        data,
                    },
                };
            }

            result.push({
                source: url,
                recognized,
            });
        });
        return result;
    } catch (err) {
        console.error(err);
        return [];
    }
};

export const recognizeURL2TitleBySDK = async (urls: string[], options: {from: 'init' | 'paste'}) => {
    const anchor = 'sdk$$';

    try {
        // 等待 flow.js 加载完成（如果正在加载中）
        // 资源加载完成，但加载失败，则直接跳过处理
        if (!flowObj.isLoadFail() && !flowObj.getFlowApi()) {
            await new Promise<void>((resolve) => {
                eventBus.once(FLOW_JS_LOADED, () => resolve());
            });
        }

        const flow = flowObj.getFlowApi();

        if (!flow) {
            throw Error('flowJS 资源加载失败');
        }

        // 非小流量用户没有 linkRecognize 方法
        if (!flowObj.isRecognizeSupported()) {
            return [];
        }

        const newUrls: string[] = [];
        urls.forEach((url) => {
            const [_, _url] = url.split(anchor);
            if (_url) {
                newUrls.push(decodeURIComponent(_url));
            }
        });

        const urlInfos: IUrlInfo[] = await flow.linkRecognize.recognize?.({
            renderType: ['TEXT_LINK'],
            urls: newUrls,
            from: options.from,
        });

        if (!Array.isArray(urlInfos)) {
            return [];
        }

        // 构造返回结果
        const result = urlInfos
            .filter((urlInfo) => urlInfo.TEXT_LINK?.userData)
            .map((urlInfo) => {
                const textLink = urlInfo.TEXT_LINK!;

                return {
                    name: textLink.userData?.name,
                    docType: 'sdk',
                    id: `${Date.now()}`, // 如果多个可能需要更唯一
                    token: anchor + encodeURIComponent(urlInfo.url),
                    avatar: textLink.userData?.icon,
                };
            });

        return result;
    } catch (err) {
        console.error(err);
        return [];
    }
};

/**
 * 底部评论对接 morpho 场景，处理文字链标题变更逻辑
 */
export const recognizeMentionTitleInComment = async (originComment: CommentItem[]) => {
    try {
        if (!flowObj.getFlowApi()) {
            await new Promise<void>((resolve) => {
                eventBus.once(FLOW_JS_LOADED, () => resolve());
            });
        }

        const flow = flowObj.getFlowApi();

        // 非小流量用户没有 linkRecognize 方法
        if (!flowObj.isRecognizeSupported()) {
            return originComment;
        }

        const comments = cloneDeep(originComment);
        const sdkUrlSet = new Set<string>();

        // 遍历评论，收集所有 sdk 链接
        comments.forEach((comment: CommentItem) => {
            if (comment.sourceType === 1) {
                try {
                    const parsedContent = JSON.parse(comment.content);
                    parsedContent[0]?.children?.forEach((item: any) => {
                        if (item.docType === 'sdk' && item.type === 'mention') {
                            const parts = item.mentionToken.split('$$');
                            if (parts[1]) {
                                sdkUrlSet.add(decodeURIComponent(parts[1]));
                            }
                        }
                    });
                } catch {
                    // 无需处理，仅避免 JSON 解析或数据出错
                }
            }
        });

        if (sdkUrlSet.size === 0) {
            return originComment;
        }

        const urlInfos: IUrlInfo[] = await flow.linkRecognize.recognize?.({
            renderType: ['TEXT_LINK'],
            urls: Array.from(sdkUrlSet),
            from: 'init',
        });

        if (!Array.isArray(urlInfos)) {
            return originComment;
        }

        const urlToTitleMap: Record<string, string> = {};
        urlInfos.forEach(urlInfo => {
            const {name} = urlInfo?.TEXT_LINK?.userData || {};
            if (name) {
                urlToTitleMap[urlInfo.url] = name;
            }
        });

        // 遍历评论，更新 sdk 链接的标题
        comments.forEach((comment: CommentItem) => {
            if (comment.sourceType === 1) {
                try {
                    comment.content = JSON.parse(comment.content);
                    comment.content[0]?.children?.forEach((item: any) => {
                        if (item.docType === 'sdk') {
                            const parts = item.mentionToken.split('$$');
                            if (parts[1]) {
                                const url = decodeURIComponent(parts[1]);
                                if (urlToTitleMap[url]) {
                                    item.mentionName = urlToTitleMap[url];
                                }
                            }
                        }
                    });
                    comment.content = JSON.stringify(comment.content);
                } catch {
                    // 无需处理，仅避免 JSON 解析或数据出错
                }
            }
        });

        return comments;
    } catch (err) {
        console.error(err);
        return originComment;
    }
};

export const reportTextLinkClick = (url: string) => {
    const flow = flowObj.getFlowApi();
    flow?.linkRecognize?.textLinkClickPV?.({url: url});
};
